import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import useAPI from '../../../api';
import { FullSizeColumn } from '../../../Components/FlexBox';
import ClientBoxInfo from './ClientBoxInfo';
import ClientInfoWidget from './ClientInfoWidget';
import DataDetailsBox from './DataDetailsBox';
import LeadOriginInfoWidget from './LeadOriginInfoWidget';
import ProductInfoWidget from './ProductInfoWidget';
import DynamicDataWidget from './DataDetailsBox/DynamicDataWidget';
import LongPracticeInfoWidget from './LongPracticeInfoWidget';
import EntityDialog from '../../../Components/Dialog/EntityInfoDialog';
import { DEFAULT_PAGES } from '../../../Components/Dialog/EntityInfoDialog';
import ConfirmPopup from '../../../Components/Popup/ConfirmPopup';
import { useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom, reloadAtom } from '../../../Atoms';
import CustomerEditDialogPage from './CustomerEditDialogPage';
import LongChannelFeaturesPage from '../../AllCustomers/Components/LongChannelFeaturesPage';
import { DYNAMIC_FEATURES_IDS } from '../../AllCustomers/Utils/constants';

const DetailsSection = function DetailsSection({
  campaignId,
  activeCampaigns,
  handleOpenCampaignSelectionDialog,
  selectedCustomer,
  contactIndex,
  handleChangeContactIndex,
  selectedEmailAddress,
  handleChangeSelectedEmailAddress,
  changePhoneOrMail,
  isLead,
  isLongChannel,
  reloadCustomer,
  branchId,
}) {
  const intl = useIntl();
  const API = useAPI();
  const setReload = useSetAtom(reloadAtom);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [customerDialog, setCustomerDialog] = useState(null);
  const [contactDialog, setContactDialog] = useState(null);
  const [confirmClosing, setConfirmClosing] = useState(null);

  const onBack = () => {
    if (contactIndex > 0) handleChangeContactIndex(contactIndex - 1);
  };
  const onNext = () => {
    if (contactIndex + 1 < selectedCustomer?.contacts.length)
      handleChangeContactIndex(contactIndex + 1);
  };

  const handleOpenCustomerDialog = () => {
    setCustomerDialog({
      id: selectedCustomer?.id,
      customerCode: selectedCustomer?.customerCode,
      companyName: selectedCustomer?.companyName,
      fiscalCode: selectedCustomer?.fiscalCode,
      vatCode: selectedCustomer?.vatCode,
      features: {
        profitability: selectedCustomer?.profitability,
        profitabilityYTD: selectedCustomer?.profitabilityYTD,
        startingProfitability: selectedCustomer?.startingProfitability,
        litigation: selectedCustomer?.litigation,
        overdraft: selectedCustomer?.overdraft,
        risk: selectedCustomer?.risk,
        disbursed: selectedCustomer?.disbursed,
        disbursedYTD: selectedCustomer?.disbursedYTD,
        ytd: selectedCustomer?.ytd,
        ytd2: selectedCustomer?.ytD2,
        cpi: selectedCustomer?.cpi,
        cvt: selectedCustomer?.cvt,
        hasNationalAgreement: selectedCustomer?.hasNationalAgreement,
        nationalAgreement: selectedCustomer?.nationalAgreement,
        segment: selectedCustomer?.category,
        dealerState: selectedCustomer?.status,
        dealerRating: selectedCustomer?.dealerRating,
        businessPackage: selectedCustomer?.businessPackage,
      },
    });
  };

  const handleOpenNewContactDialog = () => {
    setContactDialog({
      entityId: selectedCustomer?.id,
    });
  };
  const handleOpenEditContactDialog = () => {
    const contact = selectedCustomer?.contacts[contactIndex];
    if (!contact) return;

    const { contactPhones, contactEmailAddresses, contactAddresses, ...rest } =
      contact;
    setContactDialog({
      entityId: selectedCustomer?.id,
      mobilePhoneNumber:
        contactPhones?.find(({ phoneTypeId }) => phoneTypeId === 1)
          ?.phoneNumber || '',
      landlinePhoneNumber:
        contactPhones?.find(({ phoneTypeId }) => phoneTypeId === 3)
          ?.phoneNumber || '',
      email:
        contactEmailAddresses?.find(({ emailTypeId }) => emailTypeId === 1)
          ?.email || '',
      ...contactAddresses?.find(({ addressTypeId }) => addressTypeId === 1),
      ...rest,
    });
  };

  const handleCloseCustomerDialog = (e, isFormDirty) => {
    //isFormDirty --> checks if the user has changed at least one fields values
    if (isFormDirty) setConfirmClosing(e.currentTarget);
    else {
      setCustomerDialog(null);
    }
  };

  const handleCloseContactDialog = (e, isFormDirty) => {
    //isFormDirty --> checks if the user has changed at least one fields values
    if (isFormDirty) setConfirmClosing(e.currentTarget);
    else {
      setContactDialog(null);
    }
  };

  const handleCloseConfirmClosing = () => {
    setConfirmClosing(null);
  };

  const handleUpdateCustomer = (data) => {
    let payload = { ...data };
    if (isLongChannel) {
      const { features, ...restPayload } = data;
      const featuresArray = Object.keys(features || {})
        .map((key) => ({
          featureId: DYNAMIC_FEATURES_IDS[key], //dynamic feature DB Id
          featureValue:
            key === 'hasNationalAgreement'
              ? features[key] === intl.messages.YES
              : features[key],
        }))
        ?.filter(
          ({ featureValue }) =>
            featureValue !== null && featureValue !== undefined
        );
      payload = {
        ...restPayload,
        dynamicFeatures: featuresArray,
      };
    }
    if (!isLongChannel && !payload?.companyCode) {
      payload = { ...payload, companyCode: payload?.fiscalCode };
    }
    API.updateCustomer(
      { ...payload, lastName: null, firstName: null }, // Long channel customers don't have first and last name fields
      () => {
        reloadCustomer();
        setReload(true);
        setCustomerDialog(null);
        openSnackbar({
          message: intl.messages.CUSTOMER_EDITED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        openSnackbar({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.EDIT_CUSTOMER_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const handleSubmitContact = (data, edit = false) => {
    const apiCall = edit ? API.updateContact : API.addContact;
    console.log('Submitting contact data:', data);
    apiCall(
      { ...data, income: data?.income !== '' ? data?.income : null },
      () => {
        reloadCustomer();
        setReload(true);
        setContactDialog(null);
        openSnackbar({
          message: edit
            ? intl.messages.CONTACT_EDITED_SUCCESSFULLY
            : intl.messages.CONTACT_ADDED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        openSnackbar({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.EDIT_CONTACT_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const data = {
    ...(isLongChannel
      ? {
          LONG_PRACTICE: {
            data: [
              {
                infos: {
                  profitability: selectedCustomer?.profitability,
                  profitabilityYTD: selectedCustomer?.profitabilityYTD,
                  startingProfitability:
                    selectedCustomer?.startingProfitability,
                  litigation: selectedCustomer?.litigation,
                  overdraft: selectedCustomer?.overdraft,
                  risk: selectedCustomer?.risk,
                  disbursed: selectedCustomer?.disbursed,
                  disbursedYTD: selectedCustomer?.disbursedYTD,
                  ytd: selectedCustomer?.ytd,
                  ytd2: selectedCustomer?.ytD2,
                  cpi: selectedCustomer?.cpi,
                  cvt: selectedCustomer?.cvt,
                },
                nationalAgreement: selectedCustomer?.nationalAgreement,
                sector: selectedCustomer?.category,
                status: selectedCustomer?.status,
                businessPackage: selectedCustomer?.businessPackage,
              },
            ],
            Widget: LongPracticeInfoWidget,
          },
        }
      : {}),
    ...(isLead
      ? {
          LEAD: {
            data: [
              {
                leadOrigin: selectedCustomer?.leadOrigin,
                createdOn: selectedCustomer?.leadOrigin,
                description: selectedCustomer?.description,
              },
            ],
            Widget: LeadOriginInfoWidget,
          },
        }
      : {}),
    ...(selectedCustomer?.products?.length > 0
      ? {
          PRODUCT: {
            data: selectedCustomer?.products,
            Widget: ProductInfoWidget,
          },
        }
      : {}),
    ...(selectedCustomer?.metadata?.length > 0
      ? {
          ADDITIONAL_DATA: {
            data: selectedCustomer?.metadata,
            Widget: DynamicDataWidget,
          },
        }
      : {}),
  };

  const isDataEmpty = Object.keys(data).length === 0;
  return (
    <>
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={handleCloseConfirmClosing}
        handleConfirm={() => {
          handleCloseCustomerDialog();
          handleCloseContactDialog();
        }}
      />
      <EntityDialog
        title={intl.messages.EDIT_CUSTOMER}
        onSubmit={handleUpdateCustomer}
        open={Boolean(customerDialog)}
        data={customerDialog}
        isLongChannel={isLongChannel}
        edit
        pages={[
          CustomerEditDialogPage,
          ...(isLongChannel ? [LongChannelFeaturesPage] : []),
        ]}
        close={handleCloseCustomerDialog}
        branchId={branchId}
      />
      <EntityDialog
        title={
          contactDialog?.id
            ? intl.messages.EDIT_CONTACT
            : intl.messages.NEW_CONTACT
        }
        onSubmit={(data) =>
          handleSubmitContact(data, Boolean(contactDialog?.id))
        }
        open={Boolean(contactDialog)}
        data={contactDialog}
        edit={Boolean(contactDialog?.id)}
        pages={DEFAULT_PAGES}
        close={handleCloseContactDialog}
        branchId={branchId}
        contact
      />
      <FullSizeColumn flex="1 1 33%" gap="1.5vh">
        <ClientBoxInfo
          campaignName={
            activeCampaigns?.find((c) => c.id === campaignId)?.description
          }
          handleOpenCampaignSelectionDialog={
            activeCampaigns.length > 1
              ? handleOpenCampaignSelectionDialog
              : undefined
          }
          grow={isDataEmpty}
          contactIndex={contactIndex}
          contactsCount={selectedCustomer?.contacts?.length || 0}
          isLongChannel={isLongChannel}
          onClickNewContact={handleOpenNewContactDialog}
          onClickEditContact={handleOpenEditContactDialog}
          onBack={selectedCustomer?.contacts?.length > 1 ? onBack : undefined}
          onNext={selectedCustomer?.contacts?.length > 1 ? onNext : undefined}
          title={
            isLongChannel
              ? selectedCustomer?.companyName
              : selectedCustomer?.fullName
          }
          widget={
            <ClientInfoWidget
              selectedCustomer={selectedCustomer}
              selectedEmailAddress={selectedEmailAddress}
              handleChangeSelectedEmailAddress={
                handleChangeSelectedEmailAddress
              }
              changePhoneOrMail={changePhoneOrMail}
              contactIndex={contactIndex}
            />
          }
          selectedCustomer={selectedCustomer}
          openDialog={handleOpenCustomerDialog}
        />
        <DataDetailsBox data={data} />
      </FullSizeColumn>
    </>
  );
};

DetailsSection.propTypes = {
  customerId: PropTypes.string,
  selectedCustomer: PropTypes.object.isRequired,
  isLead: PropTypes.bool,
  selectedEmailAddress: PropTypes.string,
  handleChangeSelectedEmailAddress: PropTypes.func,
  changePhoneOrMail: PropTypes.func,
};

export default DetailsSection;
